/**
 * Model Context Tool Registry Helper
 * 
 * Provides safe registration and unregistration of tools
 * to prevent duplicate registration errors
 */

// Track registered tools to prevent duplicates
const registeredTools = new Set<string>();

interface ToolConfig {
  name: string;
  description: string;
  inputSchema: any;
  annotations?: any;
  execute: (args: any) => Promise<any>;
}

/**
 * Safely register a tool with the model context
 * Prevents duplicate registration errors
 * 
 * @param toolConfig - Tool configuration object
 * @returns boolean - true if successfully registered, false if already registered or error
 */
export const registerTool = (toolConfig: ToolConfig): boolean => {
  const { name } = toolConfig;

  try {
    // Check if model context is available
    if (!(window.navigator as any)?.modelContext) {
      console.warn(`[ToolRegistry] Model context not available for tool: ${name}`);
      return false;
    }

    // Check if already registered in our tracker
    if (registeredTools.has(name)) {
      console.warn(`[ToolRegistry] Tool "${name}" already registered, skipping`);
      return false;
    }

    // Attempt to register
    (window.navigator as any).modelContext?.registerTool(toolConfig);
    
    // Add to our tracker
    registeredTools.add(name);
    console.log(`[ToolRegistry] ✅ Tool "${name}" registered successfully`);
    
    return true;
  } catch (error: any) {
    // Handle duplicate registration error gracefully
    if (error.message?.includes('Duplicate tool name')) {
      console.warn(`[ToolRegistry] Tool "${name}" already registered in model context`);
      registeredTools.add(name); // Add to tracker to prevent future attempts
      return false;
    }
    
    console.error(`[ToolRegistry] ❌ Failed to register tool "${name}":`, error);
    return false;
  }
};

/**
 * Safely unregister a tool from the model context
 * 
 * @param toolName - Name of the tool to unregister
 * @returns boolean - true if successfully unregistered, false otherwise
 */
export const unregisterTool = (toolName: string): boolean => {
  try {
    // Check if model context is available
    if (!(window.navigator as any)?.modelContext?.unregisterTool) {
      console.warn(`[ToolRegistry] Model context or unregisterTool not available for: ${toolName}`);
      return false;
    }

    // Attempt to unregister
    (window.navigator as any).modelContext.unregisterTool(toolName);
    
    // Remove from our tracker
    registeredTools.delete(toolName);
    console.log(`[ToolRegistry] ✅ Tool "${toolName}" unregistered successfully`);
    
    return true;
  } catch (error) {
    console.error(`[ToolRegistry] ❌ Failed to unregister tool "${toolName}":`, error);
    return false;
  }
};

/**
 * Check if a tool is registered
 * 
 * @param toolName - Name of the tool to check
 * @returns boolean - true if registered, false otherwise
 */
export const isToolRegistered = (toolName: string): boolean => {
  return registeredTools.has(toolName);
};

/**
 * Get all registered tool names
 * 
 * @returns Array of registered tool names
 */
export const getRegisteredTools = (): string[] => {
  return Array.from(registeredTools);
};

/**
 * Clear all tracked tools (useful for testing or reset)
 * Does not actually unregister from model context
 */
export const clearToolRegistry = (): void => {
  registeredTools.clear();
  console.log('[ToolRegistry] Registry cleared');
};
